import React, { useState, useEffect } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SystemPromptService, SystemPromptRecord } from "@/services/systemPromptService";
import { Loader2, Info, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface SystemPromptEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  programKey: string;
  programName: string;
  onPromptUpdated?: () => void;
}

export const SystemPromptEditModal: React.FC<SystemPromptEditModalProps> = ({
  isOpen,
  onClose,
  locationId,
  programKey,
  programName,
  onPromptUpdated
}) => {
  const [prompt, setPrompt] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastEditedAt, setLastEditedAt] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load existing prompt when modal opens
  useEffect(() => {
    if (isOpen && locationId && programKey) {
      loadExistingPrompt();
    }
  }, [isOpen, locationId, programKey]);

  // Track changes
  useEffect(() => {
    setHasUnsavedChanges(prompt !== originalPrompt);
  }, [prompt, originalPrompt]);

  const loadExistingPrompt = async () => {
    try {
      setIsLoading(true);
      
      const existingPrompt = await SystemPromptService.getPromptForProgram(locationId, programKey);
      
      if (existingPrompt) {
        setPrompt(existingPrompt.prompt);
        setOriginalPrompt(existingPrompt.prompt);
        setLastEditedAt(existingPrompt.lastEditedAt);
        console.log(`[SystemPromptEditModal] Loaded existing prompt for ${programKey}`);
      } else {
        // Use default template
        const defaultPrompt = SystemPromptService.getDefaultPromptTemplate();
        setPrompt(defaultPrompt);
        setOriginalPrompt(defaultPrompt);
        setLastEditedAt(null);
        console.log(`[SystemPromptEditModal] Using default prompt template for ${programKey}`);
      }
      
    } catch (error) {
      console.error("[SystemPromptEditModal] Error loading prompt:", error);
      toast.error("Failed to load system prompt");
      // Fallback to default template
      const defaultPrompt = SystemPromptService.getDefaultPromptTemplate();
      setPrompt(defaultPrompt);
      setOriginalPrompt(defaultPrompt);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!prompt.trim()) {
      toast.error("System prompt cannot be empty");
      return;
    }

    try {
      setIsSaving(true);
      
      await SystemPromptService.updatePromptForProgram(locationId, {
        prompt: prompt.trim(),
        programKey
      });
      
      setOriginalPrompt(prompt.trim());
      setLastEditedAt(new Date());
      setHasUnsavedChanges(false);
      
      toast.success("System prompt saved successfully");
      onPromptUpdated?.();
      
    } catch (error) {
      console.error("[SystemPromptEditModal] Error saving prompt:", error);
      toast.error("Failed to save system prompt");
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = () => {
    const defaultPrompt = SystemPromptService.getDefaultPromptTemplate();
    setPrompt(defaultPrompt);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to close without saving?"
      );
      if (!confirmDiscard) return;
    }
    
    // Reset state when closing
    setPrompt("");
    setOriginalPrompt("");
    setLastEditedAt(null);
    setHasUnsavedChanges(false);
    onClose();
  };

  const modalTitle = `Edit AI System Prompt - ${programName}`;
  const modalDescription = (
    <div className="space-y-2">
      <p className="text-slate-700">
        Customize the AI system prompt for extracting session dates from web content for this program.
      </p>
      <p className="text-slate-600 text-sm">
        Use <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">{"{{PROGRAM_JSON}}"}</code> as a placeholder - it will be replaced with the actual program data when the AI processes content.
      </p>
      {lastEditedAt && (
        <p className="text-slate-500 text-xs">
          Last edited: {format(lastEditedAt, 'MMM d, yyyy at h:mm a')}
        </p>
      )}
    </div>
  );

  return (
    <BigFormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      description={modalDescription}
      onSave={hasUnsavedChanges ? handleSavePrompt : undefined}
      saveLabel={hasUnsavedChanges ? "Save prompt" : "No changes"}
      isSaving={isSaving}
      hideActions={isLoading}
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span>Loading system prompt...</span>
            </div>
          </div>
        ) : (
          <>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>How it works:</strong> The AI will receive this prompt along with the sanitized HTML content from the webpage. 
                The <code>{"{{PROGRAM_JSON}}"}</code> placeholder will be replaced with the specific program details (age ranges, requirements, etc.) 
                to help the AI identify relevant sessions for this program.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="system-prompt" className="text-sm font-medium">
                  System Prompt
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResetToDefault}
                    className="flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset to default
                  </Button>
                </div>
              </div>
              
              <Textarea
                id="system-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter the AI system prompt for session date extraction..."
                className="min-h-[400px] font-mono text-sm"
              />
              
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{prompt.length} characters</span>
                {hasUnsavedChanges && (
                  <span className="text-amber-600 font-medium">• Unsaved changes</span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </BigFormModal>
  );
}; 